import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SplashScreen } from '@capacitor/splash-screen';
import { Capacitor } from '@capacitor/core';

import { ThemeModeProvider } from '@/contexts/ThemeModeContext';
import { LanguageProvider } from '@/i18n';
import { AppToaster } from '@/components/feedback/AppToaster';
import { router } from '@/routes';
import { useCapacitorApp } from '@/hooks/useCapacitorApp';
import { NetworkStatusProvider } from '@/mobile/components/NetworkStatusProvider';
import { OfflineBanner } from '@/mobile/components/OfflineBanner';
import { AppUpdateDialog } from '@/mobile/components/AppUpdateDialog';
import { initCrashReporting } from '@/mobile/services/crash.service';
import { initPushService, registerDeviceWithBackend } from '@/mobile/services/push.service';
import { useAppUpdate } from '@/mobile/hooks/useAppUpdate';
import { useOfflineFlush } from '@/mobile/hooks/useOfflineFlush';
import { useAuthStore } from '@/store/authStore';
import { useUiStore } from '@/store/uiStore';
import { PermissionProvider } from '@/permissions/PermissionContext';
import { APP_NAME } from '@/constants';
import { hideBootLoader } from '@/components/common/AppLoader';

import { PropertyTypeProvider } from '@/context/PropertyTypeContext';
import { bootstrapAuthLifecycle } from '@/utils/biometric';
import { useBrandingStore, applyFavicon } from '@/store/brandingStore';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function MobileBootstrap() {
  useCapacitorApp();
  useOfflineFlush();
  const { result, showDialog, dismiss } = useAppUpdate(true);
  const accessToken = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    const stopCrash = initCrashReporting();
    void initPushService();
    void bootstrapAuthLifecycle();

    const bootNative = async () => {
      if (!Capacitor.isNativePlatform()) return;
      const currentTheme = useUiStore.getState().themeMode;
      const { syncSystemBars } = await import('@/mobile/services/systemBar.service');
      await syncSystemBars(currentTheme);
      try {
        await SplashScreen.hide();
      } catch {
        // SplashScreen plugin may not be present in web preview
      }
    };


    void bootNative();
    return () => stopCrash();
  }, []);

  useEffect(() => {
    if (accessToken) {
      void registerDeviceWithBackend(null);
    }
  }, [accessToken]);

  return (
    <>
      <OfflineBanner />
      <RouterProvider router={router} />
      <AppToaster />
      <AppUpdateDialog open={showDialog} result={result} onClose={dismiss} />
    </>
  );
}

export default function App() {
  useEffect(() => {
    const { faviconUrl, siteName, fetchBranding } = useBrandingStore.getState();
    document.title = siteName || APP_NAME;
    applyFavicon(faviconUrl);
    void fetchBranding();
    hideBootLoader();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <PermissionProvider>
          <PropertyTypeProvider>
            <ThemeModeProvider>
              <NetworkStatusProvider>
                <MobileBootstrap />
              </NetworkStatusProvider>
            </ThemeModeProvider>
          </PropertyTypeProvider>
        </PermissionProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}
